/**
 * Apertura de caja.
 *
 * Pantalla obligatoria antes de tomar el primer pedido, porque el backend
 * rechaza abrir órdenes sin turno (`422 REGLA_NEGOCIO`). No es burocracia: sin
 * base inicial declarada, el arqueo del cierre no significa nada.
 *
 * Es el único punto del flujo de venta que exige conexión. El turno lleva
 * consecutivo y arqueo, así que es un concepto del servidor; se abre una vez
 * al empezar el día y de ahí en adelante se puede trabajar sin señal.
 */

import { useState } from 'react';
import * as api from '../api/endpoints';
import { ErrorApi, ErrorRed } from '../api/cliente';
import { cop } from '../lib/formato';
import type { Turno } from '../tipos/contrato';

const BASES = [100_000, 150_000, 200_000, 300_000];

interface Props {
  nombreRestaurante: string;
  cajero: string;
  onAbierto: (turno: Turno) => void;
  onCerrarSesion: () => void;
}

export function AperturaCaja({ nombreRestaurante, cajero, onAbierto, onCerrarSesion }: Props) {
  const [base, setBase] = useState('100000');
  const [notas, setNotas] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [abriendo, setAbriendo] = useState(false);

  const monto = Number(base) || 0;

  const abrir = async () => {
    setError(null);
    setAbriendo(true);
    try {
      onAbierto(await api.abrirTurno(monto, notas.trim() || undefined));
    } catch (e) {
      if (e instanceof ErrorRed) {
        setError('Sin conexión. Abrir la caja necesita comunicarse con el servidor.');
      } else if (e instanceof ErrorApi) {
        setError(e.message);
      } else {
        setError('No se pudo abrir la caja.');
      }
    } finally {
      setAbriendo(false);
    }
  };

  return (
    <div className="login">
      <div className="card" style={{ maxWidth: 420 }}>
        <div className="marca">CN</div>
        <h1>Abrir caja</h1>
        <p className="sub">
          {nombreRestaurante} · {cajero}
        </p>

        {error && <div className="error-caja">{error}</div>}

        <div className="field">
          <label htmlFor="base">Base inicial en el cajón</label>
          <input
            id="base"
            className="mono"
            inputMode="numeric"
            value={base}
            onChange={(e) => setBase(e.target.value.replace(/\D/g, '').slice(0, 9))}
            style={{ fontSize: 20, fontWeight: 700 }}
          />
        </div>

        <div className="quick" style={{ marginBottom: 14 }}>
          {BASES.map((b) => (
            <button key={b} className="mono" onClick={() => setBase(String(b))}>
              {cop(b)}
            </button>
          ))}
        </div>

        <div className="field">
          <label htmlFor="notas">Notas (opcional)</label>
          <input
            id="notas"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ej. turno de la tarde"
          />
        </div>

        <button
          className="btn btn-primary"
          style={{ width: '100%', padding: 13, marginTop: 4 }}
          disabled={abriendo || monto <= 0}
          onClick={() => void abrir()}
        >
          {abriendo ? 'Abriendo…' : `Abrir caja con ${cop(monto)}`}
        </button>

        <button
          className="btn btn-ghost"
          style={{ width: '100%', padding: 11, marginTop: 8 }}
          onClick={onCerrarSesion}
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
